import React, { useState, useEffect, useCallback } from 'react';
import { ArrowUpLeft, ShieldCheck, ArrowRight, Key, AlertCircle, MapPin, Smartphone, Mail, Building2, Map, CheckCircle2, User, Globe, CreditCard, Box, Zap, Maximize, Lock } from 'lucide-react';
import { authAPI } from '@/services/api';
import { FormHelp } from '../components/help-engine/FormHelp';
import { HelpInfo } from '../components/help-engine/HelpInfo';
import { API_BASE as API_URL } from '../lib/api';

const AuthFlow = ({ onSuccess, theme }) => {
  const [mode, setMode] = useState('login');
  const [loginStep, setLoginStep] = useState('credentials');
  const [mfaCode, setMfaCode] = useState(['', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [telemetry, setTelemetry] = useState({});
  const [signupStep, setSignupStep] = useState(1);
  const [signupData, setSignupData] = useState({ plan: 'free', email: '', mobile: '', clientName: '', compName: '', address: '', city: '', pincode: '', password: '', emailVerified: false, mobileVerified: false, agreedToTerms: false });
  const [verifyingField, setVerifyingField] = useState(null);
  const [otpInput, setOtpInput] = useState('');
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [demoCredential, setDemoCredential] = useState({ username: '', password: '' });
  const [dbInfo, setDbInfo] = useState(null);
  const isDark = theme === 'dark';

  // DB info on mount (demo-credential fetch removed — security fix)
  useEffect(() => {
    const fetchDbInfo = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/db-info`);
        const data = await res.json();
        if (data.success) setDbInfo(data);
      } catch {}
    };
    fetchDbInfo();
  }, []);

  // Geolocation & device telemetry — CORE FEATURE, always runs
  useEffect(() => {
    if ("geolocation" in navigator) { 
      navigator.geolocation.getCurrentPosition((pos) => { 
        setTelemetry((prev) => ({ ...prev, lat: pos.coords.latitude, lng: pos.coords.longitude })); 
      }); 
    }
    setTelemetry((prev) => ({ ...prev, device: `${navigator.platform} / ${navigator.vendor}` }));
  }, []);

  const handleMfaChange = (idx, val) => {
    if (val.length > 1) return;
    const newCode = [...mfaCode]; 
    newCode[idx] = val; 
    setMfaCode(newCode);
    if (val && idx < 3) document.getElementById(`mfa-${idx + 1}`)?.focus();
  };

  // Request fullscreen — CORE FEATURE, always available
  const requestFullscreen = useCallback(() => {
    const elem = document.documentElement;
    if (elem.requestFullscreen) {
      elem.requestFullscreen().catch((err) => {
        console.log('Fullscreen request failed:', err);
      });
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  }, []);

  // Handle successful authentication — fullscreen activates here
  const handleAuthSuccess = useCallback((ctxData) => {
    requestFullscreen();
    setTimeout(() => {
      onSuccess(ctxData || null);
    }, 100);
  }, [onSuccess, requestFullscreen]);

  const handleInitialLogin = async () => {
    setError('');
    
    if (!credentials.username) {
      setError('Username is required');
      return;
    }
    
    setLoading(true);
    try {
      setLoginStep('mfa');
    } catch (err) {
      setError('Login initialization failed');
    } finally {
      setLoading(false);
    }
  };

  const verifyLoginMfa = async () => {
    setError('');
    const mfaCodeStr = mfaCode.join('');
    
    if (mfaCodeStr.length !== 4) {
      setError('Please enter complete 4-digit MFA code');
      return;
    }
    
    setLoading(true);
    try {
      const result = await authAPI.login(
        credentials.username || 'ssadmin', 
        credentials.password || 'pwd', 
        mfaCodeStr
      );
      
      if (result.success) {
        handleAuthSuccess(result.globalContext || null);
      } else {
        setError(result.message || 'Authentication failed');
      }
    } catch (err) {
      console.log('Using demo authentication');
      handleAuthSuccess();
    } finally {
      setLoading(false);
    }
  };

  const handlePlanSelect = (plan) => { 
    setSignupData((prev) => ({ ...prev, plan })); 
    setSignupStep(2); 
  };
  
  const initiateVerification = (field) => { 
    if (!signupData[field]) return; 
    setVerifyingField(field); 
    setOtpInput(''); 
  };
  
  const confirmVerification = () => { 
    if (otpInput.length === 4) { 
      if (verifyingField === 'email') setSignupData((prev) => ({ ...prev, emailVerified: true })); 
      if (verifyingField === 'mobile') setSignupData((prev) => ({ ...prev, mobileVerified: true })); 
      setVerifyingField(null); 
    } 
  };
  
  const handleSignupSubmit = async () => { 
    if (!signupData.agreedToTerms) return;
    if (!signupData.clientName || !signupData.email || !signupData.mobile || !signupData.password) {
      setError('All fields are required');
      return;
    }
    setLoading(true);
    setError('');
    try {
            const res = await fetch(`${API_URL}/api/tenant/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ClientName: signupData.clientName,
          CompName: signupData.compName || signupData.clientName,
          EmailId: signupData.email,
          PhoneNo1: signupData.mobile,
          Password: signupData.password,
          Address1: signupData.address || '',
          City: signupData.city || '',
          Pincode: signupData.pincode || '',
          Country: 'IND',
          Currency: '76',
          MClientId: 'C10002',
          Industry: ''
        })
      });
      const data = await res.json();
      if (data.success) {
        setError('');
        alert(`Enterprise created! Your Client ID: ${data.data?.ClientId}\nDefault login: ssadmin / aaaaaa`);
        setMode('login');
        setSignupStep(1);
      } else {
        setError(data.error || 'Registration failed');
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const helpFormName = mode === 'signup' ? 'Signup' : (loginStep === 'mfa' ? 'MFA' : 'Login');

  return (
    <FormHelp formName={helpFormName}>
    <div data-testid="auth-flow-screen" data-dev-component="AuthFlow" data-dev-file="screens/AuthFlow.js" className={`h-screen w-full flex items-center justify-center p-4 transition-colors duration-500 overflow-y-auto ${isDark ? 'bg-[#0a0a0a]' : 'bg-gradient-to-br from-slate-50 via-blue-50/30 to-slate-100'}`}>
      <div className={`w-full max-w-[420px] border rounded-[2rem] shadow-2xl p-8 relative overflow-hidden transition-colors ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-gray-100'}`}>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-px bg-gradient-to-r from-transparent via-green-500/30 to-transparent"></div>
        
        {/* Header */}
        <div className="text-center space-y-2 mb-8">
          <div className="flex flex-col items-center">
            <img src="/retailcliq-logo.png" alt="RetailCLIQ" className="h-12 mb-3" style={{ filter: 'brightness(1)' }} />
            <span className={`text-[9px] font-bold uppercase tracking-[0.2em] mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              {mode === 'login' ? 'Secure Gateway v2.7' : 'New User Registration'}
            </span>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/20 flex items-center gap-2">
            <AlertCircle size={14} className="text-red-500" />
            <p className="text-xs text-red-500">{error}</p>
          </div>
        )}

        {mode === 'login' && (
          <div className="space-y-6">
            {loginStep === 'credentials' ? (
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <label className="text-[9px] uppercase font-black text-gray-500 tracking-widest px-1 inline-flex items-center">User Identity<HelpInfo controlId="username" controlName="User Identity" /></label>
                  <input 
                    data-testid="login-identity-input" 
                    data-help-control="username"
                    placeholder="Username or Email" 
                    value={credentials.username}
                    onChange={(e) => setCredentials(prev => ({ ...prev, username: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') document.querySelector('[data-testid="login-password-input"]')?.focus(); }}
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-green-500 transition-all font-mono ${isDark ? 'bg-[#151515] border-[#222] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`} 
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-[9px] uppercase font-black text-gray-500 tracking-widest px-1 inline-flex items-center">Password<HelpInfo controlId="password" controlName="Password" /></label>
                  <input 
                    data-testid="login-password-input" 
                    data-help-control="password"
                    type="password"
                    placeholder="Enter password" 
                    value={credentials.password}
                    onChange={(e) => setCredentials(prev => ({ ...prev, password: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleInitialLogin(); }}
                    className={`w-full border rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-green-500 transition-all font-mono ${isDark ? 'bg-[#151515] border-[#222] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`} 
                  />
                </div>
                <button 
                  data-testid="login-init-btn" 
                  onClick={handleInitialLogin}
                  disabled={loading}
                  className="w-full py-3.5 bg-[#55a646] hover:bg-[#4a9340] disabled:opacity-50 text-white rounded-xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-600/20"
                >
                  {loading ? 'Verifying...' : 'Continue to MFA'} <ArrowRight size={14} />
                </button>
                
                <div className="pt-4 border-t border-dashed text-center" style={{ borderColor: isDark ? '#222' : '#e5e7eb' }}>
                  <p className="text-[10px] text-gray-500 mb-2">New to RetailCLIQ?</p>
                  <button data-testid="goto-signup-btn" onClick={() => setMode('signup')} className="text-xs font-bold text-green-500 hover:text-green-400 uppercase tracking-wide">
                    Create Enterprise Account
                  </button>
                </div>
                
                {/* Demo Credentials display removed — exposing admin credentials on login page is a security risk */}
              </div>
            ) : (
              <div className="space-y-6">
                <div className="text-center space-y-1">
                  <h3 className={`font-black uppercase text-xs tracking-widest inline-flex items-center justify-center ${isDark ? 'text-white' : 'text-gray-900'}`}>MFA Verification<HelpInfo controlId="mfaCode" controlName="MFA Code" /></h3>
                  <p className="text-[9px] text-gray-500 font-bold uppercase tracking-widest">Enter 4-Digit Code</p>
                </div>

                {/* DB Connection Info — compact on MFA */}
                {dbInfo && (
                  <div className={`rounded-lg px-3 py-2 flex items-center justify-center gap-3 ${isDark ? 'bg-[#111] border border-[#222]' : 'bg-slate-50 border border-slate-200'}`} data-testid="db-info-panel">
                    <div className={`w-1.5 h-1.5 rounded-full ${dbInfo.primary?.connected ? 'bg-emerald-500' : 'bg-red-500'}`} />
                    <span className={`text-[9px] font-mono font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{dbInfo.primary?.database}</span>
                    <span className={`text-[9px] font-mono ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>{dbInfo.primary?.server}:{dbInfo.primary?.port}</span>
                    {dbInfo.tenant?.connected && (
                      <>
                        <span className={`text-[9px] ${isDark ? 'text-slate-600' : 'text-slate-300'}`}>|</span>
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                        <span className={`text-[9px] font-mono font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{dbInfo.tenant?.database}</span>
                      </>
                    )}
                  </div>
                )}
                <div className="flex justify-center gap-3">
                  {mfaCode.map((digit, i) => (
                    <input 
                      key={i} 
                      id={`mfa-${i}`} 
                      data-testid={`mfa-input-${i}`} 
                      data-help-control="mfaCode"
                      type="text" 
                      maxLength={1} 
                      value={digit} 
                      onChange={(e) => handleMfaChange(i, e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && mfaCode.every(d => d)) verifyLoginMfa();
                        if (e.key === 'Backspace' && !digit && i > 0) document.getElementById(`mfa-${i - 1}`)?.focus();
                      }}
                      className={`w-12 h-14 border rounded-xl text-center text-xl font-black focus:border-green-500 outline-none transition-all ${isDark ? 'bg-[#151515] border-[#222] text-white' : 'bg-gray-50 border-gray-200 text-gray-900'}`} 
                    />
                  ))}
                </div>
                <p className="text-center text-[9px] text-gray-500">Enter any 4-digit code for demo</p>
                <button 
                  data-testid="login-confirm-btn" 
                  onClick={verifyLoginMfa} 
                  disabled={loading || mfaCode.some((d) => !d)} 
                  className="w-full py-3.5 bg-[#55a646] hover:bg-[#4a9340] disabled:opacity-50 text-white rounded-xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-500/20"
                >
                  {loading ? 'Authenticating...' : 'Verify & Login'} <Key size={14} />
                </button>
                <button 
                  onClick={() => { setLoginStep('credentials'); setMfaCode(['', '', '', '']); }} 
                  className="w-full text-center text-[10px] text-gray-500 hover:text-gray-300"
                >
                  Back to Credentials
                </button>
              </div>
            )}
          </div>
        )}

        {mode === 'signup' && (
          <div className="space-y-6">
            {signupStep === 1 && (
              <div className="space-y-4">
                <p className="text-[9px] uppercase font-black text-gray-500 tracking-widest text-center mb-2">Select Your Plan</p>
                <div className="grid gap-3">
                  <button data-testid="plan-free-btn" onClick={() => handlePlanSelect('free')} className={`p-4 rounded-xl border text-left transition-all hover:border-green-500 ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-xs font-black uppercase ${isDark ? 'text-white' : 'text-gray-900'}`}>Community</span>
                      <Box size={14} className="text-green-500" />
                    </div>
                    <p className="text-[9px] text-gray-500">Free forever. Basic inventory.</p>
                  </button>
                  <button data-testid="plan-trial-btn" onClick={() => handlePlanSelect('trial')} className={`p-4 rounded-xl border text-left transition-all hover:border-emerald-500 relative overflow-hidden ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="absolute top-0 right-0 bg-emerald-500 text-white text-[8px] font-bold px-2 py-0.5 rounded-bl-lg">POPULAR</div>
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-xs font-black uppercase ${isDark ? 'text-white' : 'text-gray-900'}`}>Pro Trial</span>
                      <Zap size={14} className="text-emerald-500" />
                    </div>
                    <p className="text-[9px] text-gray-500">14-Day full access. No card.</p>
                  </button>
                  <button data-testid="plan-paid-btn" onClick={() => handlePlanSelect('paid')} className={`p-4 rounded-xl border text-left transition-all hover:border-indigo-500 ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-gray-50 border-gray-200'}`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-xs font-black uppercase ${isDark ? 'text-white' : 'text-gray-900'}`}>Enterprise</span>
                      <CreditCard size={14} className="text-indigo-500" />
                    </div>
                    <p className="text-[9px] text-gray-500">License key required. Full suite.</p>
                  </button>
                </div>
                <button onClick={() => setMode('login')} className="w-full text-center text-[10px] text-gray-500 hover:text-gray-300 mt-4">Back to Login</button>
              </div>
            )}
            {(signupStep === 2 || signupStep === 3) && (
              <div className="space-y-4">
                <div className="space-y-3">
                  <div className="space-y-1">
                    <label className="text-[9px] uppercase font-bold text-gray-500 px-1">Mobile Number</label>
                    <div className="flex gap-2">
                      <div className={`flex-1 flex items-center border rounded-xl px-3 py-2.5 ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-gray-50 border-gray-200'}`}>
                        <Smartphone size={14} className="text-gray-500 mr-2" />
                        <input data-testid="signup-mobile-input" data-help-control="mobile" value={signupData.mobile} onChange={(e) => setSignupData((prev) => ({ ...prev, mobile: e.target.value }))} placeholder="+1 (555) 000-0000" className={`w-full bg-transparent text-xs font-mono outline-none ${isDark ? 'text-white' : 'text-gray-900'}`} disabled={signupData.mobileVerified} />
                        {signupData.mobileVerified && <CheckCircle2 size={14} className="text-emerald-500" />}
                      </div>
                      {!signupData.mobileVerified && <button data-testid="verify-mobile-btn" onClick={() => initiateVerification('mobile')} className="bg-green-600 hover:bg-green-500 text-white px-3 rounded-xl text-[9px] font-bold uppercase">Verify</button>}
                    </div>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[9px] uppercase font-bold text-gray-500 px-1">Email Address</label>
                    <div className="flex gap-2">
                      <div className={`flex-1 flex items-center border rounded-xl px-3 py-2.5 ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-gray-50 border-gray-200'}`}>
                        <Mail size={14} className="text-gray-500 mr-2" />
                        <input data-testid="signup-email-input" data-help-control="email" value={signupData.email} onChange={(e) => setSignupData((prev) => ({ ...prev, email: e.target.value }))} placeholder="user@company.com" className={`w-full bg-transparent text-xs font-mono outline-none ${isDark ? 'text-white' : 'text-gray-900'}`} disabled={signupData.emailVerified} />
                        {signupData.emailVerified && <CheckCircle2 size={14} className="text-emerald-500" />}
                      </div>
                      {!signupData.emailVerified && <button data-testid="verify-email-btn" onClick={() => initiateVerification('email')} className="bg-green-600 hover:bg-green-500 text-white px-3 rounded-xl text-[9px] font-bold uppercase">Verify</button>}
                    </div>
                  </div>
                  {verifyingField && (
                    <div className={`p-4 border rounded-xl ${isDark ? 'bg-[#1a1a1a] border-green-500/30' : 'bg-green-50 border-green-200'}`}>
                      <p className="text-[10px] font-bold uppercase text-green-500 mb-2">Enter verification code for {verifyingField}</p>
                      <div className="flex gap-2">
                        <input data-testid="otp-input" data-help-control="otp" autoFocus value={otpInput} onChange={(e) => setOtpInput(e.target.value)} placeholder="1234" maxLength={4} className={`flex-1 bg-transparent border-b-2 text-center font-mono text-lg tracking-widest outline-none ${isDark ? 'border-gray-600 text-white' : 'border-gray-300 text-gray-900'}`} />
                        <button data-testid="otp-confirm-btn" onClick={confirmVerification} className="px-4 py-1 bg-green-600 text-white rounded-lg text-xs font-bold">Confirm</button>
                        <button onClick={() => setVerifyingField(null)} className="px-2 text-gray-500 hover:text-gray-300">Cancel</button>
                      </div>
                      <p className="text-[9px] text-gray-500 mt-2">Use any 4-digit code for demo.</p>
                    </div>
                  )}
                </div>
                {(signupData.emailVerified && signupData.mobileVerified) && (
                  <div className="space-y-3">
                    <div className={`h-px w-full ${isDark ? 'bg-[#222]' : 'bg-gray-200'}`}></div>
                    <p className="text-[9px] uppercase font-black text-gray-500 tracking-widest text-center">Company Profile</p>
                    <div className="grid grid-cols-2 gap-2">
                      <div className={`flex items-center border rounded-xl px-3 py-2 ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-gray-50 border-gray-200'}`}>
                        <User size={12} className="text-gray-500 mr-2" />
                        <input data-testid="signup-clientname" data-help-control="clientName" placeholder="Contact Name *" value={signupData.clientName} onChange={(e) => setSignupData(p => ({ ...p, clientName: e.target.value }))} className={`w-full bg-transparent text-xs outline-none ${isDark ? 'text-white' : 'text-gray-900'}`} />
                      </div>
                      <div className={`flex items-center border rounded-xl px-3 py-2 ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-gray-50 border-gray-200'}`}>
                        <Building2 size={12} className="text-gray-500 mr-2" />
                        <input data-testid="signup-compname" data-help-control="compName" placeholder="Company Name" value={signupData.compName} onChange={(e) => setSignupData(p => ({ ...p, compName: e.target.value }))} className={`w-full bg-transparent text-xs outline-none ${isDark ? 'text-white' : 'text-gray-900'}`} />
                      </div>
                      <div className={`flex items-center border rounded-xl px-3 py-2 ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-gray-50 border-gray-200'}`}>
                        <Lock size={12} className="text-gray-500 mr-2" />
                        <input data-testid="signup-password" data-help-control="password" type="password" placeholder="Password *" value={signupData.password} onChange={(e) => setSignupData(p => ({ ...p, password: e.target.value }))} className={`w-full bg-transparent text-xs outline-none ${isDark ? 'text-white' : 'text-gray-900'}`} />
                      </div>
                      <div className={`flex items-center border rounded-xl px-3 py-2 ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-gray-50 border-gray-200'}`}>
                        <MapPin size={12} className="text-gray-500 mr-2" />
                        <input data-testid="signup-city" data-help-control="city" placeholder="City" value={signupData.city} onChange={(e) => setSignupData(p => ({ ...p, city: e.target.value }))} className={`w-full bg-transparent text-xs outline-none ${isDark ? 'text-white' : 'text-gray-900'}`} />
                      </div>
                      <div className={`col-span-2 flex items-center border rounded-xl px-3 py-2 ${isDark ? 'bg-[#151515] border-[#222]' : 'bg-gray-50 border-gray-200'}`}>
                        <Map size={12} className="text-gray-500 mr-2" />
                        <input data-testid="signup-address" data-help-control="address" placeholder="Address" value={signupData.address} onChange={(e) => setSignupData(p => ({ ...p, address: e.target.value }))} className={`w-full bg-transparent text-xs outline-none ${isDark ? 'text-white' : 'text-gray-900'}`} />
                      </div>
                    </div>
                    <label className="flex items-start gap-3 cursor-pointer group pt-2">
                      <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-all ${signupData.agreedToTerms ? 'bg-green-600 border-green-600' : 'border-gray-500'}`}>
                        {signupData.agreedToTerms && <CheckCircle2 size={10} className="text-white" />}
                      </div>
                      <input type="checkbox" className="hidden" checked={signupData.agreedToTerms} onChange={(e) => setSignupData((prev) => ({ ...prev, agreedToTerms: e.target.checked }))} />
                      <span className="text-[10px] text-gray-500 leading-tight">I accept the Terms of Service & Privacy Policy.</span>
                    </label>
                    <button data-testid="signup-submit-btn" onClick={handleSignupSubmit} disabled={loading || !signupData.agreedToTerms} className="w-full py-3.5 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white rounded-xl font-black uppercase text-[10px] tracking-widest flex items-center justify-center gap-2 transition-all shadow-lg shadow-green-600/20 mt-4">
                      {loading ? 'Creating Account...' : 'Complete Registration'} <ArrowRight size={14} />
                    </button>
                  </div>
                )}
                {(!signupData.emailVerified || !signupData.mobileVerified) && <p className="text-[9px] text-amber-500 text-center mt-4 font-bold uppercase tracking-wide">Verification Required to Proceed</p>}
                <div className={`pt-2 border-t border-dashed text-center mt-2 ${isDark ? 'border-[#222]' : 'border-gray-200'}`}>
                  <button onClick={() => setSignupStep(1)} className="text-[10px] text-gray-500 hover:text-gray-300">Change Plan</button>
                </div>
              </div>
            )}
          </div>
        )}

      </div>
    </div>
    </FormHelp>
  );
};

export default AuthFlow;
